import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { LegalLayout } from './LegalLayout';
import { LEGAL } from '../../config/legal';

/** One renderable piece of a legal document. Shapes come from the locale catalogs. */
type Block =
  | { type: 'p'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'table'; head: string[]; rows: string[][] };

interface Section {
  heading: string;
  blocks: Block[];
}

interface DocumentContent {
  title: string;
  updated: string;
  callout?: string;
  sections: Section[];
}

/**
 * Values available to the catalogs as {{placeholders}}, so the operating company and
 * contact addresses are defined once in `config/legal.ts` rather than in four languages.
 */
const vars: Record<string, string> = {
  entity: LEGAL.entityName,
  brand: LEGAL.brandName,
  privacyEmail: LEGAL.privacyEmail,
  supportEmail: LEGAL.supportEmail,
  region: LEGAL.dataRegion,
  date: LEGAL.effectiveDate,
};

const substitute = (text: string) =>
  text.replace(/{{(\w+)}}/g, (whole, key) => vars[key] ?? whole);

/**
 * Minimal inline markup so translators can keep emphasis and links without HTML:
 * `**bold**` and `[label](href)`. Anything else renders literally.
 */
function inline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const pattern = /\*\*(.+?)\*\*|\[(.+?)\]\((.+?)\)/g;
  let cursor = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > cursor) parts.push(text.slice(cursor, match.index));
    if (match[1] !== undefined) {
      parts.push(<strong key={key++}>{match[1]}</strong>);
    } else {
      parts.push(
        <a key={key++} href={match[3]}>
          {match[2]}
        </a>
      );
    }
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
}

const render = (text: string) => inline(substitute(text));

function renderBlock(block: Block, key: number) {
  switch (block.type) {
    case 'h3':
      return <h3 key={key}>{render(block.text)}</h3>;
    case 'ul':
      return (
        <ul key={key}>
          {block.items.map((item, i) => (
            <li key={i}>{render(item)}</li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <table key={key}>
          <thead>
            <tr>
              {block.head.map((cell, i) => (
                <th key={i}>{render(cell)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j}>{render(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    case 'p':
    default:
      return <p key={key}>{render(block.text)}</p>;
  }
}

/**
 * Renders one legal document from the `legal` i18n namespace. Falls back to English
 * automatically (i18next `fallbackLng`) if a translation is missing.
 */
export function LegalDocument({ doc }: { doc: 'privacy' | 'terms' | 'impressum' }) {
  const { t } = useTranslation('legal');
  const content = t(doc, { returnObjects: true }) as DocumentContent;

  // A missing namespace would otherwise render "[object Object]" or a raw key.
  if (!content || typeof content !== 'object' || !Array.isArray(content.sections)) {
    return null;
  }

  return (
    <LegalLayout title={content.title} updated={substitute(content.updated)}>
      {content.callout && (
        <div className="legal-callout">
          <p>{render(content.callout)}</p>
        </div>
      )}
      {content.sections.map((section, i) => (
        <section key={i}>
          <h2>{render(section.heading)}</h2>
          {section.blocks.map(renderBlock)}
        </section>
      ))}
    </LegalLayout>
  );
}
